import classNames from 'classnames';
import styles from './dropdown.module.css';
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { CopyToClipboard } from '@product/CopyToClipboard/CopyToClipboard';
import { TextInput } from '@common/TextInput/TextInput';

export interface GroupedDropdownValues {
  group: string;
  values: Map<string, string>;
}

export interface DropdownProps {
  label?: string;
  selected?: string;
  copyToClipboard?: boolean;
  values: string[] | Map<string, string> | GroupedDropdownValues[];
  id: string;
  className?: string;
  onChange: (newValue: string, key?: string) => void;
  name?: string;
  testidSuffix?: string;
  isReadonly?: boolean;
  preventDefault?: boolean;
}

const isGroupedDropdownValuesArray = (
  values: string[] | Map<string, string> | GroupedDropdownValues[]
): values is GroupedDropdownValues[] =>
  Array.isArray(values) && (values as GroupedDropdownValues[])?.every(item => item.group !== undefined);

const isStringValuesArray = (values: string[] | Map<string, string> | GroupedDropdownValues[]): values is string[] =>
  Array.isArray(values) && (values as string[])?.every(item => typeof item === 'string');

const isMap = (values: string[] | Map<string, string> | GroupedDropdownValues[]): values is Map<string, string> =>
  values instanceof Map;

const getActualValues = (values: string[] | Map<string, string> | GroupedDropdownValues[]): string[] => {
  if (isMap(values)) {
    return [...values.values()];
  }

  if (isGroupedDropdownValuesArray(values)) {
    return values.flatMap(value => Array.from(value.values.values()));
  }

  return values;
};

const renderOptionsForValues = (values: string[] | Map<string, string> | GroupedDropdownValues[]): ReactNode => {
  const renderSingleOption = (key: string, value: string) => (
    <option data-testid="dropdown-select-option" key={key} value={value} className="border border-gray-500">
      {key}
    </option>
  );

  if (isMap(values)) {
    return <>{Array.from(values).map(([key, value]) => renderSingleOption(key, value))}</>;
  }

  if (isStringValuesArray(values)) {
    return <>{values.map(value => renderSingleOption(value, value))}</>;
  }

  if (isGroupedDropdownValuesArray(values)) {
    return (
      <>
        {values.map(val => (
          <optgroup label={val.group}>
            {Array.from(val.values).map(([key, value]) => renderSingleOption(key, value))}
          </optgroup>
        ))}
      </>
    );
  }

  return <div />;
};

export const Dropdown = ({
  label,
  selected,
  copyToClipboard,
  values,
  id,
  className,
  onChange,
  name,
  testidSuffix,
  isReadonly,
  preventDefault,
}: DropdownProps) => {
  const elementId = id;

  useEffect(() => {
    if (selected) {
      if (!getActualValues(values).includes(selected)) {
        onChange('');
      }
    }
  });

  const testIdSuffix = testidSuffix ? `-${testidSuffix}` : '';
  return (
    <div>
      {label && (
        <div className="flex flex-row mb-4">
          <label htmlFor={elementId} className="block">
            {label}
          </label>
          {copyToClipboard && <CopyToClipboard text={selected ?? ''} copiedLabel={label} />}
        </div>
      )}
      {isReadonly ? (
        <>
          <TextInput value={selected ?? ''} disabled styles="h-16 mt-4 mb-8" />
          {/* this div is necessary to stretch the width of the input to show the full sku */}
          <div className="text-2xl px-6 border-l border-r whitespace-nowrap h-0 overflow-hidden">{selected}</div>
        </>
      ) : (
        <select
          name={name}
          id={elementId}
          key={elementId}
          data-testid={`dropdown-select${testIdSuffix}`}
          onChange={event => {
            if (preventDefault) {
              event.preventDefault();
            }
            onChange(event.target.value, event.target.name);
          }}
          value={selected}
          className={classNames(
            'appearance-none',
            'outline-none',
            'focus:outline-none',
            'block',
            'w-full',
            'text-sm',
            'font-normal',
            'text-gray-300',
            'text-left',
            'border',
            'border-gray-200',
            'mb-8',
            'h-16',
            'pl-4',
            'pr-12',
            'hover:border-gray-500',
            'focus:border-gray-500',
            'bg-white',
            className,
            styles.dropdown
          )}
        >
          {renderOptionsForValues(values)}
        </select>
      )}
    </div>
  );
};
